import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Badge } from '../../UI';

const statusStages = [
  'Quotation Raised',
  'Approved',
  'Repair Assigned',
  'Repair Concluded',
  'Delivered'
];

export default function StatusBar({ jobCard }) {
  let currentStatusIndex = 0;

  // Check approval status
  if (jobCard?.approvalStatus === 'Yes') {
    currentStatusIndex = 1; // Move to "Approved" stage
  }

  // Check the tracker array to find the latest state
  if (jobCard?.tracker && jobCard?.tracker.length > 0) {
    // Check if there is a "Task Completed" state in the tracker
    const taskCompletedIndex = jobCard.tracker.findIndex(
      (tracker) => tracker.State === 'Task Completed'
    );

    if (taskCompletedIndex !== -1) {
      // Set index to "Repair Concluded" stage if "Task Completed" is found
      currentStatusIndex = Math.max(currentStatusIndex, 3); // "Repair Concluded" is at index 3
    } else {
      // Check if there is a "Task Assigned" state in the tracker
      const taskAssignedIndex = jobCard.tracker.findIndex(
        (tracker) => tracker.State === 'Task Assigned'
      );

      if (taskAssignedIndex !== -1) {
        // Set index to the "Repair Assigned" stage if "Task Assigned" is found
        currentStatusIndex = Math.max(currentStatusIndex, 2); // "Repair Assigned" is at index 2
      }
    }

    // Find the last state in the tracker and check if it's further along
    const lastTrackerState = jobCard.tracker[jobCard.tracker.length - 1].State;
    const trackerIndex = statusStages.findIndex(stage => stage === lastTrackerState);

    // Update the current status index if the tracker state is further along
    if (trackerIndex > currentStatusIndex) {
      currentStatusIndex = trackerIndex;
    }
  }

  // Check if both deliveryDate and exitMileage exist, then update status to "Delivered"
  if (jobCard?.deliveryDate && jobCard?.exitMileage) {
    currentStatusIndex = Math.max(currentStatusIndex, 4); // "Delivered" is at index 4
  }

  const progressPercentage = ((currentStatusIndex + 1) / statusStages.length) * 100;

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h4 className="text-base font-semibold text-gray-900">Job Progress</h4>
          <p className="text-sm text-gray-600 mt-1">
            Current Stage: <span className="font-medium text-primary-600">{statusStages[currentStatusIndex]}</span>
          </p>
        </div>
        <Badge variant="primary" size="lg">
          {Math.round(progressPercentage)}% Complete
        </Badge>
      </div>

      {/* Progress Bar */}
      <div className="mb-6">
        <div className="overflow-hidden h-3 rounded-full bg-gray-200">
          <div
            style={{ width: `${progressPercentage}%` }}
            className="h-full rounded-full bg-gradient-to-r from-primary-500 to-primary-600 transition-all duration-500 ease-out shadow-sm"
          />
        </div>
      </div>

      {/* Status Steps */}
      <div className="hidden md:block">
        <div className="grid grid-cols-5 gap-4">
          {statusStages.map((stage, index) => (
            <div key={stage} className="relative">
              <div className="flex flex-col items-center">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-all duration-300
                  ${index <= currentStatusIndex
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'bg-gray-200 text-gray-400'
                  }
                `}>
                  {index <= currentStatusIndex ? (
                    <CheckCircleIcon className="h-6 w-6" />
                  ) : (
                    <span className="text-sm font-semibold">{index + 1}</span>
                  )}
                </div>
                <span className={`
                  text-xs text-center font-medium transition-colors duration-300
                  ${index <= currentStatusIndex ? 'text-primary-600' : 'text-gray-500'}
                `}>
                  {stage}
                </span>
              </div>
              {index < statusStages.length - 1 && (
                <div className="absolute top-5 left-1/2 w-full h-0.5 -z-10">
                  <div className={`
                    h-full transition-all duration-500
                    ${index < currentStatusIndex ? 'bg-primary-600' : 'bg-gray-200'}
                  `} />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Status Steps */}
      <div className="md:hidden space-y-2">
        {statusStages.map((stage, index) => (
          <div key={stage} className="flex items-center space-x-3">
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
              ${index <= currentStatusIndex
                ? 'bg-primary-600 text-white'
                : 'bg-gray-200 text-gray-400'
              }
            `}>
              {index <= currentStatusIndex ? (
                <CheckCircleIcon className="h-5 w-5" />
              ) : (
                <span className="text-xs font-semibold">{index + 1}</span>
              )}
            </div>
            <span className={`
              text-sm font-medium
              ${index <= currentStatusIndex ? 'text-primary-600' : 'text-gray-500'}
            `}>
              {stage}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
